import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import { ButtonOBSOLETED } from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import {
  Filters,
  FilterInput,
  FilterSelect,
  FilterSelectAsync,
  FilterTimePicker,
} from "./Filters";
import { getQueryStringParameters } from "../../common/utils";
import { DisplayDate, DisplayGuid } from "../items";

const dayOfWeekOptions = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
].map((day) => ({ value: day, label: day }));

const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "Paused", label: "Paused" },
  { value: "Cancelled", label: "Cancelled" },
];

const AutomatedBookingAgreementsInternal = (props) => {
  const [loadedData, setLoadedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadData = async () => {
    const { history, location } = props;
    const filters = getQueryStringParameters(location.search);
    setIsLoading(false);
    const data = await props.api.automatedBookingAgreements.searchAutomatedBookingAgreements({
      filters,
    });
    setLoadedData(data);
    setIsLoading(true);
    return data;
  };

  useEffect(() => {
    loadData();
  }, [props.location.search]);

  const lookupFacility = async (inputValue) => {
    const response = await props.api.facilities.lookupByNameOrId(inputValue);
    return response;
  };

  const { location, history } = props;
  const filters = getQueryStringParameters(location.search);
  const page = parseInt(filters.page, 10) || 1;
  const orderBy = filters.orderBy || "createdAt";
  const orderDirection = filters.orderDirection || "desc";

  const orderHandler = (key) => {
    let direction;
    if (key.toLowerCase() === orderBy.toLowerCase()) {
      direction = orderDirection === "desc" ? "asc" : "desc";
    } else {
      direction = "desc";
    }

    Filters.goToPage(history, location, page, key, direction);
  };

  const pagingHandler = async (action) => {
    if (!loadedData) {
      return;
    }

    if (page < loadedData.pages && action === "add") {
      Filters.goToPage(history, location, page + 1, orderBy, orderDirection);
    }
    if (page > 1 && action === "sub") {
      Filters.goToPage(history, location, page - 1, orderBy, orderDirection);
    }
  };

  const header = [
    {
      name: "id",
      label: "Agreement Id",
      canOrder: true,
      breakWord: false,
      render: ({ id }) => (
        <DisplayGuid baseAnchor={"/automated-booking-agreement"}>{id}</DisplayGuid>
      ),
    },
    {
      name: "facilityName",
      label: "Facility",
      canOrder: false,
      breakWord: false,
      render: ({ facilityName }) => <span>{facilityName}</span>,
    },
    {
      name: "productName",
      label: "Product",
      canOrder: false,
      breakWord: true,
      render: ({ productName }) => <span>{productName}</span>,
    },
    {
      name: "userName",
      label: "User",
      canOrder: false,
      breakWord: false,
      render: ({ userName }) => <span>{userName}</span>,
    },
    {
      name: "userEmail",
      label: "User Email",
      canOrder: false,
      breakWord: true,
      render: ({ userEmail }) => <span>{userEmail}</span>,
    },
    {
      name: "dayOfWeek",
      label: "Day",
      canOrder: true,
      breakWord: false,
      render: ({ dayOfWeek }) => <span>{dayOfWeek}</span>,
    },
    {
      name: "timeOfDay",
      label: "Time",
      canOrder: true,
      breakWord: false,
      render: ({ timeOfDay }) => <span>{timeOfDay}</span>,
    },
    {
      name: "durationMinutes",
      label: "Duration (min)",
      canOrder: false,
      breakWord: false,
      render: ({ durationMinutes }) => <span>{durationMinutes}</span>,
    },
    {
      name: "preferredCourtName",
      label: "Preferred Court",
      canOrder: false,
      breakWord: true,
      render: ({ preferredCourtName }) => <span>{preferredCourtName}</span>,
    },
    {
      name: "createdAt",
      label: "Created (CET)",
      canOrder: true,
      breakWord: false,
      render: ({ createdAtCet }) => <DisplayDate breakTime>{createdAtCet}</DisplayDate>,
    },
    {
      name: "startDateCet",
      label: "Start (CET)",
      canOrder: false,
      breakWord: false,
      render: ({ startDateCet }) => <DisplayDate breakTime>{startDateCet}</DisplayDate>,
    },
    {
      name: "endDateCet",
      label: "End (CET)",
      canOrder: false,
      breakWord: false,
      render: ({ endDateCet }) => <DisplayDate breakTime>{endDateCet}</DisplayDate>,
    },
    {
      name: "status",
      label: "Status",
      canOrder: true,
      breakWord: false,
      render: ({ status }) => <span>{status}</span>,
    },
    {
      name: "attempts",
      label: "Attempts",
      canOrder: false,
      breakWord: false,
      render: ({ successfulAttemptCount, attemptCount }) => (
        <span>
          {successfulAttemptCount}/{attemptCount}
        </span>
      ),
    },
    {
      name: "lastAttempt",
      label: "Last Attempt",
      canOrder: false,
      breakWord: false,
      render: ({ lastAttemptResult, lastAttemptStartedAtCet }) => (
        <span>
          {lastAttemptResult || "-"}
          {lastAttemptStartedAtCet ? (
            <>
              <br />
              <DisplayDate breakTime>{lastAttemptStartedAtCet}</DisplayDate>
            </>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <div className="content-with-right-pane-delimiter">
            <PageTop
              documentTitle="Automated Booking Agreements"
              documentDescription="Automated Booking Agreements"
            />
            <h2 className="main-page-header">Automated Booking Agreements</h2>
            <Filters
              initFromUrl
              submitUrl
              validateForm={(formData, updatedKey) => {
                if (updatedKey !== "id") {
                  return formData;
                }
                return { id: formData.id };
              }}
              render={(formData, update) => (
                <div className="filters-block">
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="id">
                      Agreement Id
                    </label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name="id"
                      placeholder="Find by id"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="facilityId">
                      Facility
                    </label>
                    <FilterSelectAsync
                      className="filter-select"
                      data={formData}
                      debounce
                      update={update}
                      name="facilityId"
                      loadOptions={lookupFacility}
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="productId">
                      Product Id
                    </label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name="productId"
                      placeholder="Find by product id"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="user">
                      User
                    </label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name="user"
                      placeholder="Filter by user name, email or id"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="status">
                      Status
                    </label>
                    <FilterSelect
                      className="filter-select"
                      data={formData}
                      update={update}
                      name="status"
                      options={statusOptions}
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="dayOfWeek">
                      Day Of Week
                    </label>
                    <FilterSelect
                      className="filter-select"
                      data={formData}
                      update={update}
                      name="dayOfWeek"
                      options={dayOfWeekOptions}
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="preferredCourtName">
                      Preferred Court
                    </label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name="preferredCourtName"
                      placeholder="Filter by preferred court"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="createdFrom">
                      Created From
                    </label>
                    <FilterTimePicker
                      className="filter-date-picker"
                      data={formData}
                      update={update}
                      name="createdFrom"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="createdTo">
                      Created To
                    </label>
                    <FilterTimePicker
                      data={formData}
                      update={update}
                      name="createdTo"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="activeFrom">
                      Active From
                    </label>
                    <FilterTimePicker
                      className="filter-date-picker"
                      data={formData}
                      update={update}
                      name="activeFrom"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="activeTo">
                      Active To
                    </label>
                    <FilterTimePicker
                      data={formData}
                      update={update}
                      name="activeTo"
                    />
                  </div>
                  <div className="filters-label-block">
                    <label className="filters-label" htmlFor="resultsPerPage">
                      Results Per Page
                    </label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name="resultsPerPage"
                      placeholder="Results per page"
                    />
                  </div>
                  <br />
                  <br />
                  <div className="filters-submit-wrapper">
                    <ButtonOBSOLETED
                      type="submit"
                      buttonContent="Search"
                      disabled={!isLoading}
                    />
                  </div>
                  <hr />
                </div>
              )}
            />
            {loadedData == null ? (
              <span>Loading...</span>
            ) : (
              <>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <input
                    style={{ width: "6%", color: "grey", fontSize: "0.85rem" }}
                    type="button"
                    value="< Prev"
                    onClick={() => pagingHandler("sub")}
                  />
                  <span style={{ color: "grey", fontSize: "0.85rem" }}>
                    Showing page {page} out of {loadedData.pages || 0}
                  </span>
                  <input
                    style={{ width: "6%", color: "grey", fontSize: "0.85rem" }}
                    type="button"
                    value="Next >"
                    onClick={() => pagingHandler("add")}
                  />
                </div>
                <br />
                <article className="data-table-wrap">
                  <table className="data-lists component-data-table cell-content-align-top">
                    <thead>
                      <tr>
                        {header.map((property) => (
                          <td
                            key={property.name}
                            style={property.canOrder ? { cursor: "pointer" } : null}
                            onClick={() =>
                              property.canOrder ? orderHandler(property.name) : null
                            }
                          >
                            {property.label}{" "}
                            {property.canOrder &&
                            orderBy.toLowerCase() === property.name.toLowerCase()
                              ? orderDirection === "desc"
                                ? "↓"
                                : "↑"
                              : ""}
                          </td>
                        ))}
                      </tr>
                    </thead>
                    {loadedData.agreements && loadedData.agreements.length > 0 ? (
                      loadedData.agreements.map((row) => (
                        <tbody key={row.id}>
                          <tr className="hover-listview ">
                            {header.map((property) => (
                              <td
                                key={property.name}
                                style={property.breakWord ? { overflowWrap: "anywhere" } : {}}
                              >
                                <strong className="table-cell-label">{property.label}</strong>
                                {property.render(row)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      ))
                    ) : (
                      <tbody>
                        <tr>
                          <td colSpan={header.length} style={{ textAlign: "center" }}>
                            No agreements found
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </table>
                </article>
              </>
            )}
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const AutomatedBookingAgreements = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(AutomatedBookingAgreementsInternal)
  )
);

AutomatedBookingAgreements.requiresLogin = true;
AutomatedBookingAgreements.propTypes = { isLoggedIn: PropTypes.bool };

export default AutomatedBookingAgreements;
